import { useId, type ReactNode } from "react";
import { paletteFor, type VizPalette } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { cn } from "../lib/cn";

/** One prior-reading reference for the delta row under the gauge. */
export interface FearGreedDelta {
  /** Short label, e.g. `Prev close` / `1W` / `1M` (localized by the caller). */
  label: ReactNode;
  /** Prior score, or `null` when upstream had no reading for that window. */
  value: number | null | undefined;
}

export interface FearGreedGaugeProps {
  /** Current index score, 0–100 (clamped). */
  score: number;
  /** CNN's rating label for the score, e.g. `Fear` / `Greed` (the text channel). */
  rating: string;
  /** Prior readings rendered as signed deltas below the gauge. */
  deltas?: FearGreedDelta[];
  theme?: Theme;
  className?: string;
}

const VIEW_W = 220;
const CX = 110;
const CY = 110;
const R = 90;
const ARC_W = 16;

/** Zone → color; the needle + big number use this, always paired with the rating text. */
function zoneColor(p: VizPalette, score: number): string {
  if (score < 25) return p.critical; // extreme fear
  if (score < 45) return p.serious; // fear
  if (score < 55) return p.warning; // neutral
  return p.good; // greed / extreme greed
}

/** Point on the gauge arc for a 0–100 score (0 = left, 100 = right, top semicircle). */
function scoreToPoint(score: number, radius: number): { x: number; y: number } {
  const clamped = Math.max(0, Math.min(100, score));
  const angle = Math.PI - (clamped / 100) * Math.PI; // π (left) → 0 (right)
  return { x: CX + radius * Math.cos(angle), y: CY - radius * Math.sin(angle) };
}

/** Signed, rounded delta text for the reference rows (`+3`, `-8`, `0`). */
function signedDelta(current: number, prev: number): string {
  const d = Math.round(current - prev);
  return d > 0 ? `+${d}` : String(d);
}

const ARROW = { up: "▲", down: "▼", flat: "■" } as const;
const ARROW_CLASS = { up: "text-good-text", down: "text-critical", flat: "text-muted" } as const;

/**
 * Semicircular CNN Fear & Greed gauge (0–100): a color-zoned arc (extreme fear →
 * extreme greed, critical→serious→warning→good ramp), a needle at the current
 * score, the numeric score + rating below, and optional signed deltas vs. prior
 * readings. Color is always paired with a second channel — the rating text and
 * the arrow/sign on every delta — so meaning survives CVD and grayscale.
 */
export function FearGreedGauge({
  score,
  rating,
  deltas = [],
  theme,
  className,
}: FearGreedGaugeProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const gradientId = useId();

  const clamped = Math.max(0, Math.min(100, score));
  const color = zoneColor(palette, clamped);
  const left = { x: CX - R, y: CY };
  const right = { x: CX + R, y: CY };
  const track = `M ${left.x} ${left.y} A ${R} ${R} 0 0 1 ${right.x} ${right.y}`;
  const needle = scoreToPoint(clamped, R - ARC_W / 2 - 2);

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${CY + 12}`}
        className="w-full max-w-[280px]"
        role="img"
        aria-label={`Fear & Greed ${Math.round(clamped)} — ${rating}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={palette.critical} />
            <stop offset="28%" stopColor={palette.serious} />
            <stop offset="50%" stopColor={palette.warning} />
            <stop offset="72%" stopColor={palette.good} />
            <stop offset="100%" stopColor={palette.good} />
          </linearGradient>
        </defs>

        {/* Faint full track under the colored arc for definition in both themes. */}
        <path
          d={track}
          fill="none"
          stroke={palette.grid}
          strokeWidth={ARC_W + 2}
          strokeLinecap="round"
        />
        <path
          d={track}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={ARC_W}
          strokeLinecap="round"
        />

        {/* Needle + hub. */}
        <line
          x1={CX}
          y1={CY}
          x2={needle.x}
          y2={needle.y}
          stroke={color}
          strokeWidth={3.5}
          strokeLinecap="round"
        />
        <circle cx={CX} cy={CY} r={7} fill={color} />
        <circle cx={CX} cy={CY} r={3} fill={palette.surface} />

        {/* End scale labels. */}
        <text x={left.x} y={CY + 12} textAnchor="middle" fontSize="10" fill={palette.muted}>
          0
        </text>
        <text x={right.x} y={CY + 12} textAnchor="middle" fontSize="10" fill={palette.muted}>
          100
        </text>
      </svg>

      <div className="-mt-2 flex flex-col items-center">
        <div className="text-4xl font-semibold tabular-nums" style={{ color }}>
          {Math.round(clamped)}
        </div>
        <div className="mt-0.5 text-sm font-medium capitalize text-ink">{rating}</div>
      </div>

      {deltas.length > 0 && (
        <div className="mt-4 flex w-full items-stretch justify-center gap-4">
          {deltas.map((d, i) => {
            const has = typeof d.value === "number";
            const dir = has
              ? clamped > d.value!
                ? "up"
                : clamped < d.value!
                  ? "down"
                  : "flat"
              : "flat";
            return (
              <div key={i} className="flex min-w-0 flex-col items-center gap-0.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
                  {d.label}
                </span>
                {has ? (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
                      ARROW_CLASS[dir],
                    )}
                  >
                    <span aria-hidden="true">{ARROW[dir]}</span>
                    <span>{signedDelta(clamped, d.value!)}</span>
                  </span>
                ) : (
                  <span className="text-xs text-muted tabular-nums">—</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
